import cn from 'classnames';
import { CSSProperties, useEffect, useMemo, useCallback, useState, useRef } from 'react';
import Loader from 'components/Loader';
import { Typo } from 'components/typography/Typo';
import { getPolicyEventsByCategory } from 'models/dashboard/api';
import { PolicyEventsByCategory as TPolicyEventsByCategory } from 'models/dashboard/dto';
import { toAbbreviatedNumber } from 'services/numbers';
import { CircleGraph } from '../CircleGraph';
import styles from './index.module.css';

const labels: { [key: string]: string } = {
	infrastructure_updates: 'Infrastructure updates',
	misconfiguration: 'Misconfiguration',
	misplaced_data: 'Misplaced data',
	data_at_risk: 'Data at risk',
	others: 'Other',
};

/*
	This widget is (for now at least) an almost exact copy of
	DataTypesOverview, except for data fetching.
*/

type Props = {
	data: TPolicyEventsByCategory;
};

function PolicyEventsByCategory(props: Props) {
	const { data } = props;

	const nodeRef = useRef();

	const [height, setHeight] = useState(144);
	const [hovered, setHovered] = useState(-1);

	const onMouseEnter = useCallback(function (e) {
		setHovered(+e.currentTarget.dataset.i);
	}, []);
	const onMouseLeave = useCallback(function (e) {
		const { i } = e.currentTarget.dataset;
		setHovered((h) => (h === +i ? -1 : h));
	}, []);

	const observer = useMemo(
		() =>
			new ResizeObserver((entries) => {
				for (const entry of entries) {
					setHeight(
						entry.contentBoxSize ? entry.contentBoxSize[0].blockSize : entry.contentRect.height
					);
				}
			}),
		[]
	);

	const handleChart = useCallback((node) => {
		if (nodeRef.current) {
			observer.unobserve(nodeRef.current);
		}

		if (!node) return;

		observer.observe(node);
		nodeRef.current = node;
	}, []);

	return (
		<div
			className={styles.container}
			ref={handleChart}
			style={{ '--graph-max-width': `${height}px` } as CSSProperties}
		>
			<div className={styles.growingChart}>
				<div className={styles.growingChartInner}>
					<CircleGraph
						data={data.categories.map((dt) => dt.events_count)}
						hovered={hovered}
						onMouseEnter={onMouseEnter}
						onMouseLeave={onMouseLeave}
					/>

					<div className={styles.totalContainer}>
						<Typo variant="D/Medium/H400-Page-Title" className={styles.totalNumber}>
							{toAbbreviatedNumber(data.total_count, 1)}
						</Typo>
						<Typo variant="D/Medium/Meta-S-CAP" color="secondary">
							Total
						</Typo>
					</div>
				</div>
			</div>

			<div className={styles.shrinkingTable}>
				<div className={styles.textColumn}>
					{data.categories.map((category, i) => (
						<span
							className={cn(styles.row, hovered === i && styles.hoveredRow)}
							key={i}
							data-i={i}
							onMouseEnter={onMouseEnter}
							onMouseLeave={onMouseLeave}
						>
							<span className={styles.square} />

							<span>{labels[category.name] || category.name}</span>
						</span>
					))}
				</div>
				<div className={styles.numberColumn}>
					{data.categories.map((category, i) => (
						<span key={i} data-i={i} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
							{toAbbreviatedNumber(category.events_count, 1)}
						</span>
					))}
				</div>
				<div className={styles.numberColumn}>
					{data.categories.map((category, i) => (
						<span key={i} data-i={i} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
							<Typo variant="D/Regular/Body-S" color="secondary">
								{toAbbreviatedNumber((category.events_count * 100) / data.total_count, 1)}%
							</Typo>
						</span>
					))}
				</div>
			</div>
		</div>
	);
}

function Wrapper() {
	const [data, setData] = useState<TPolicyEventsByCategory | null>(null);

	useEffect(() => {
		getPolicyEventsByCategory().then((res) => setData(res));
	}, []);

	return data === null ? (
		<Loader />
	) : data.total_count === 0 ? (
		<Typo className={styles.noAnalytics} variant="D/Regular/Body-S" color="secondary">
			No available analytics
		</Typo>
	) : (
		<PolicyEventsByCategory data={data} />
	);
}

export { Wrapper as PolicyEventsByCategory };
